import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { DatePickerField } from "@/components/DatePickerField";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Truck, X, RotateCcw, AlertTriangle, Printer } from "lucide-react";
import { ORDER_STATUS_BADGE } from "../dealer/DealerOrders";
import { CancellationReviewPanel } from "@/components/admin/CancellationReviewPanel";
import { RefundStatus } from "@/components/orders/RefundStatus";
import { ORDER_STATUS, human } from "@/lib/statusLabels";
import { AmendmentReviewPanel } from "@/components/admin/AmendmentReviewPanel";
import { DiscontinuedBadge } from "@/components/catalog/DiscontinuedBadge";
import { OrderSettlementCard } from "@/components/settlement/SupplierSettlement";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [shipForm, setShipForm] = useState({ shipment_type: "parcel", carrier: "", service: "", tracking_number: "", pieces: "", estimated_delivery: "" });
  const [splitApproved, setSplitApproved] = useState(false);
  const [handoffConfirmed, setHandoffConfirmed] = useState(true);
  const [evtForm, setEvtForm] = useState({ sid: null, state: "IN_TRANSIT", source: "manual", note: "" });
  const [dialog, setDialog] = useState(null); // "release" | "refund" | "review"
  const [reason, setReason] = useState("");
  const [reviewAction, setReviewAction] = useState("extend");
  const [extendDays, setExtendDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [ovPrices, setOvPrices] = useState({});
  const [ovReason, setOvReason] = useState("");
  const [ovAck, setOvAck] = useState(false);
  const [ovWarnings, setOvWarnings] = useState([]);
  const [freightOpen, setFreightOpen] = useState(false);
  const [freightForm, setFreightForm] = useState({ amount: "", note: "" });
  const [dcDialog, setDcDialog] = useState(null); // "approve" | "decline" | "dccancel"
  const [dcForm, setDcForm] = useState({ note: "", reason: "", refund_deposit: false, refund_balance: false, release_allocation: true, allocation_note: "", exception_ack: false });

  const load = () =>
    api
      .get(`/admin/orders/${id}`)
      .then((res) => setOrder(res.data))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        navigate("/admin/orders");
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setShipField = (k) => (e) => setShipForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleLine = (lid) => setSelected((s) => (s.includes(lid) ? s.filter((x) => x !== lid) : [...s, lid]));
  const shippable = order && ["PAID", "PARTIALLY_FULFILLED"].includes(order.status);  // refund-locked orders never ship
  const readyLines = shippable ? order.lines.filter((l) => l.fulfillment_status === "READY") : [];
  const othersAwaiting = order ? order.lines.some((l) => !selected.includes(l.line_id) && l.fulfillment_status === "AWAITING_RECEIPT") : false;

  const todayLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

  const handleShip = async (e) => {
    e.preventDefault();
    if (shipForm.estimated_delivery && shipForm.estimated_delivery < todayLocal()) {
      toast.error("Estimated delivery cannot be in the past");
      return;
    }
    if (shipForm.pieces !== "" && shipForm.pieces != null && !/^\d+$/.test(String(shipForm.pieces).trim()) || (shipForm.pieces && parseInt(shipForm.pieces, 10) < 1)) {
      toast.error(shipForm.shipment_type === "freight" ? "Enter a whole number of pallets (1 or more)." : "Enter a whole number of packages (1 or more).");
      return;
    }
    if (shipForm.tracking_url && !/^https:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}(\/.*)?$/.test(shipForm.tracking_url.trim())) {
      toast.error("Tracking URL must be a valid https:// carrier link");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/orders/${id}/shipments`, {
        line_ids: selected,
        shipment_type: shipForm.shipment_type,
        carrier: shipForm.carrier,
        service: shipForm.service,
        tracking_number: shipForm.tracking_number,
        ...(shipForm.pieces ? { pieces: parseInt(shipForm.pieces, 10) } : {}),
        ...(shipForm.tracking_url ? { tracking_url: shipForm.tracking_url.trim() } : {}),
        ...(shipForm.estimated_delivery ? { estimated_delivery: shipForm.estimated_delivery } : {}),
        split_shipment: splitApproved,
        handoff_confirmed: handoffConfirmed,
      });
      toast.success(handoffConfirmed ? `Shipment created — ${selected.length} line(s) dispatched, inventory reduced` : `Shipment packed — ${selected.length} line(s) awaiting carrier handoff (no inventory movement)`);
      setShipOpen(false);
      setSelected([]);
      setShipForm({ shipment_type: "parcel", carrier: "", service: "", tracking_number: "", pieces: "", estimated_delivery: "" });
      setSplitApproved(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (dialog === "release") {
        await api.post(`/admin/orders/${id}/release`, { reason });
        toast.success("Order released — commitment returned to available inventory");
      } else if (dialog === "refund") {
        const { data } = await api.post(`/admin/orders/${id}/refund`, { reason });
        const res = data.refund_result;
        if (res?.outcome === "FAILED") toast.error(res.message);
        else toast.success(res?.message || "Refund submitted");
      } else if (dialog === "review") {
        await api.post(`/admin/orders/${id}/resolve-review`, { action: reviewAction, extend_days: parseInt(extendDays, 10), note: reason });
        toast.success("Review resolved");
      }
      setDialog(null);
      setReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openOverride = () => {
    setOvPrices(Object.fromEntries(order.lines.map((l) => [l.line_id, String(l.trade_price_per_box)])));
    setOvReason("");
    setOvAck(false);
    setOvWarnings([]);
    setOverrideOpen(true);
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/orders/${id}/price-override`, {
        lines: Object.entries(ovPrices).map(([line_id, v]) => ({ line_id, price_per_box: parseFloat(v) })),
        reason: ovReason,
        acknowledge_warning: ovAck,
      });
      toast.success("Price override applied — snapshotted and audited");
      setOverrideOpen(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.warnings) {
        setOvWarnings(d.warnings);
        toast.error("Below floor/target margin — review and confirm explicitly");
      } else {
        toast.error(formatApiErrorDetail(d));
      }
    } finally {
      setSaving(false);
    }
  };

  const submitFreight = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${id}/freight`, { amount_usd: parseFloat(freightForm.amount), note: freightForm.note });
      toast.success("Freight quote updated — order total recalculated");
      setFreightOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitDc = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (dcDialog === "approve") {
        await api.post(`/admin/orders/${id}/cancellation-request/approve`, {
          note: dcForm.note, refund_deposit: dcForm.refund_deposit, refund_balance: dcForm.refund_balance,
          release_allocation: dcForm.release_allocation, allocation_note: dcForm.allocation_note,
          exception_ack: dcForm.exception_ack,
        });
        toast.success("Cancellation approved — treatment recorded and audited");
      } else if (dcDialog === "decline") {
        await api.post(`/admin/orders/${id}/cancellation-request/decline`, { reason: dcForm.reason });
        toast.success("Cancellation request declined");
      } else if (dcDialog === "dccancel") {
        await api.post(`/admin/orders/${id}/direct-cancel`, {
          reason: dcForm.reason, refund_deposit: dcForm.refund_deposit, refund_balance: dcForm.refund_balance,
          release_allocation: dcForm.release_allocation, allocation_note: dcForm.allocation_note,
          exception_ack: dcForm.exception_ack,
        });
        toast.success("Order cancelled — financial and allocation treatment recorded");
      }
      setDcDialog(null);
      setDcForm({ note: "", reason: "", refund_deposit: false, refund_balance: false, release_allocation: true, allocation_note: "", exception_ack: false });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleReadyToShip = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/orders/${id}/ready-to-ship`);
      toast.success("Marked ready to ship — the 70% balance is now due");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!order) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-order-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const releasable = ["CHECKOUT_HOLD", "PAYMENT_PROCESSING", "ADMIN_REVIEW"].includes(order.status);

  return (
    <div data-testid="admin-order-detail">
      <Button variant="ghost" onClick={() => navigate("/admin/orders")} data-testid="back-to-admin-orders" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> Orders
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-light tracking-tight text-charcoal" data-testid="admin-order-number">{order.order_number}</h1>
          <p className="mt-1 text-xs text-muted-foreground">{order.dealer_name} · {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3">
          {order.fulfillment_mode === "FULL_CONTAINER" && (
            <span className="badge-status badge-available" data-testid="admin-container-mode-badge">Full Container</span>
          )}
          <span className={`badge-status ${ORDER_STATUS_BADGE[order.status]}`} data-testid="admin-order-status">{human(ORDER_STATUS, order.status)}</span>
          {order.status === "CHECKOUT_HOLD" && (
            <Button size="sm" variant="outline" onClick={openOverride} data-testid="price-override-button" className="border-limestone text-charcoal hover:bg-sand">
              Adjust pricing
            </Button>
          )}
          {order.status === "CHECKOUT_HOLD" && order.fulfillment_mode === "FULL_CONTAINER" && (
            <Button size="sm" variant="outline" onClick={() => { setFreightForm({ amount: String(order.totals.freight_usd ?? ""), note: "" }); setFreightOpen(true); }} data-testid="freight-edit-button" className="border-limestone text-charcoal hover:bg-sand">
              Set freight
            </Button>
          )}
          {order.status === "CONFIRMED" && order.fulfillment_mode === "FULL_CONTAINER" && (
            <Button size="sm" onClick={handleReadyToShip} disabled={saving} data-testid="ready-to-ship-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
              <Truck className="mr-1 h-3.5 w-3.5" /> Ready to ship — balance due
            </Button>
          )}
          {order.direct && order.fulfillment_mode === "FULL_CONTAINER" && ["CONFIRMED", "BALANCE_DUE", "PAID"].includes(order.status) && (
            <Button size="sm" variant="outline" onClick={() => setDcDialog("dccancel")} data-testid="direct-cancel-button" className="border-limestone text-status-sold hover:bg-sand">
              <X className="mr-1 h-3.5 w-3.5" /> Cancel container order
            </Button>
          )}
          {releasable && (
            <Button size="sm" variant="outline" onClick={() => setDialog("release")} data-testid="release-order-button" className="border-limestone text-status-sold hover:bg-sand">
              <X className="mr-1 h-3.5 w-3.5" /> Release
            </Button>
          )}
          {["PAID", "REFUND_FAILED"].includes(order.status) && order.refund?.status !== "SUCCEEDED" && (
            <Button size="sm" variant="outline" onClick={() => setDialog("refund")} data-testid="refund-order-button" className="border-limestone text-status-sold hover:bg-sand">
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> {order.status === "REFUND_FAILED" ? "Retry refund" : "Refund"}
            </Button>
          )}
          {order.status === "ADMIN_REVIEW" && (
            <Button size="sm" onClick={() => setDialog("review")} data-testid="resolve-review-button" className="bg-terracotta text-white hover:bg-terracotta-hover">Resolve review</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/orders/${id}/packing-slip`)} data-testid="admin-packing-slip-button" className="border-limestone text-charcoal hover:bg-sand">
            <Printer className="mr-1 h-3.5 w-3.5" /> Packing slip
          </Button>
        </div>
      </div>

      <AmendmentReviewPanel order={order} onDone={load} />
      <RefundStatus order={order} admin />
      <CancellationReviewPanel order={order} onDone={load} />

      {order.direct?.cancellation_request?.status === "PENDING" && order.fulfillment_mode === "FULL_CONTAINER" && (
        <Card className="mt-6 border-terracotta/40 bg-white" data-testid="dc-request-banner">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <p className="text-sm text-charcoal">
              <span className="font-medium">Cancellation requested by the dealer.</span>
              {order.direct.cancellation_request.reason ? ` Reason: ${order.direct.cancellation_request.reason}` : ""}
              {" "}Deposit is non-refundable in this window unless you explicitly authorize a refund.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setDcDialog("approve")} data-testid="dc-approve-button" className="bg-terracotta text-white hover:bg-terracotta-hover">Approve</Button>
              <Button size="sm" variant="outline" onClick={() => setDcDialog("decline")} data-testid="dc-decline-button" className="border-limestone text-charcoal hover:bg-sand">Decline</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {(order.payment_reversal || order.settled_after_release) && (
        <Card className="mt-6 border-status-sold/40 bg-white" data-testid="review-flags">
          <CardContent className="flex items-center gap-3 p-4 text-sm text-status-sold">
            <AlertTriangle className="h-4 w-4" />
            {order.payment_reversal && "ACH reversal/dispute reported by Stripe — inventory unchanged, resolve manually."}
            {order.settled_after_release && "Payment settled after the hold was released — refund via Stripe and reconcile."}
          </CardContent>
        </Card>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border bg-white lg:col-span-2">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Lines</p>
            <div className="mt-4 space-y-3">
              {order.lines.map((l) => {
                const othersWaiting = order.lines.some((x) => x.line_id !== l.line_id && x.fulfillment_status === "AWAITING_RECEIPT");
                return (
                  <div key={l.line_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background p-4" data-testid={`admin-order-line-${l.line_id}`}>
                    <div>
                      <p className="text-sm font-medium text-charcoal">{l.variant_label}<DiscontinuedBadge variantId={l.variant_id} /></p>
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                        {l.sku} · {l.boxes} boxes × ${l.trade_price_per_box.toFixed(2)}/box = ${l.line_subtotal.toFixed(2)} ·{" "}
                        {l.source === "FLORIDA" ? "Florida" : `incoming · ${l.container_number}`}
                      </p>
                      {l.shipped_tracking && <p className="mt-0.5 font-mono text-xs text-charcoal">{l.shipped_carrier} {l.shipped_tracking}</p>}
                      {l.pricing && (
                        <p className="mt-0.5 text-[11px] text-warmgrey" data-testid={`line-pricing-${l.line_id}`}>
                          {l.pricing.source} · base ${l.pricing.base_price?.toFixed(2)}
                          {l.pricing.premium_total ? ` + $${l.pricing.premium_total.toFixed(2)} premium` : ""}
                          {l.pricing.pallets ? ` · ${l.pricing.pallets} pallet(s)` : ""}
                          {l.pricing.floor_warning ? " · below floor/target — acknowledged" : ""}
                          {l.pricing.margin_pct != null ? ` · GM ${l.pricing.margin_pct}%` : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="badge-status badge-neutral" data-testid={`admin-line-status-${l.line_id}`}>{l.fulfillment_status}</span>
                      {shippable && l.fulfillment_status === "READY" && (
                        <label className="flex items-center gap-2 text-xs font-medium text-charcoal">
                          <input type="checkbox" data-testid={`ship-check-${l.line_id}`} checked={selected.includes(l.line_id)} onChange={() => toggleLine(l.line_id)} />
                          Ship
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {readyLines.length > 0 && (
              <div className="mt-4">
                <Button onClick={() => { setShipOpen(true); setSplitApproved(false); }} disabled={selected.length === 0} data-testid="ship-selected-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                  <Truck className="mr-1 h-4 w-4" /> Ship selected lines{selected.length > 0 ? ` (${selected.length})` : ""}
                </Button>
              </div>
            )}
            <div className="mt-4 flex justify-end border-t border-border pt-4">
              <div className="text-right text-sm text-muted-foreground" data-testid="admin-totals-block">
                {order.totals.freight_usd != null && (
                  <>
                    <p>Product subtotal <span className="ml-3 text-charcoal" data-testid="admin-order-subtotal">${order.totals.subtotal_usd.toFixed(2)}</span></p>
                    <p className="mt-1">Freight & logistics ({(order.totals.freight_status || "").toLowerCase()}) <span className="ml-3 text-charcoal" data-testid="admin-order-freight">${order.totals.freight_usd.toFixed(2)}</span></p>
                  </>
                )}
                <p className="mt-1">
                  Total <span className="font-display text-xl text-charcoal" data-testid="admin-order-total">${(order.totals.grand_total_usd ?? order.totals.total_usd).toFixed(2)}</span>
                  {order.totals.tax_status === "UNRESOLVED" && <span className="ml-2 text-xs">(tax calculated at checkout)</span>}
                  {order.totals.tax_status === "EXEMPT" && (
                    <span className="ml-2 text-xs text-status-available" data-testid="admin-order-tax-exempt">tax exempt · resale certificate{order.tax?.certificate_state ? ` (${order.tax.certificate_state})` : ""}</span>
                  )}
                  {(order.totals.tax_status === "COLLECTED" || order.totals.tax_status === "NOT_COLLECTED") && (
                    <span className="ml-2 text-xs" data-testid="admin-order-tax">sales tax ${(order.totals.tax_amount ?? 0).toFixed(2)}</span>
                  )}
                  {order.payment?.captured_usd != null && <span className="ml-2 text-xs font-medium text-charcoal" data-testid="admin-order-paid-total">· total charged ${order.payment.captured_usd.toFixed(2)}</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <OrderSettlementCard orderId={order.id} />
          <Card className="border-border bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Ship to (frozen)</p>
              <p className="mt-3 text-sm font-medium text-charcoal" data-testid="admin-ship-to-name">{order.ship_to.recipient_name}</p>
              {order.ship_to.company && <p className="text-xs text-muted-foreground">{order.ship_to.company}</p>}
              <p className="mt-1 text-xs text-muted-foreground" data-testid="admin-ship-to-address">
                {order.ship_to.address?.line1}{order.ship_to.address?.line2 ? `, ${order.ship_to.address.line2}` : ""}, {order.ship_to.address?.city}
                {order.ship_to.address?.state ? `, ${order.ship_to.address.state}` : ""} {order.ship_to.address?.postal_code}
              </p>
              {order.ship_to.kind === "direct_to_client" && <p className="mt-2 text-xs font-medium text-terracotta">Direct to client — packing slip excludes trade pricing</p>}
            </CardContent>
          </Card>

          <Card className="border-border bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Payment</p>
              <p className="mt-3 text-sm text-charcoal" data-testid="admin-payment-method">{order.payment.method ? order.payment.method.toUpperCase() : "—"} · ${(order.payment.captured_usd ?? order.payment.amount_usd)?.toFixed(2)}{order.payment.captured_usd != null && order.payment.captured_usd !== order.payment.amount_usd ? ` charged (order $${order.payment.amount_usd?.toFixed(2)} + tax/freight)` : ""}</p>
              {order.ach_deadline_at && <p className="mt-1 text-xs text-muted-foreground">ACH deadline: {new Date(order.ach_deadline_at).toLocaleDateString()}</p>}
            </CardContent>
          </Card>

          {order.direct && (
            <Card className="border-border bg-white" data-testid="dc-terms-card">
              <CardContent className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Direct-container terms</p>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="flex justify-between"><span className="text-muted-foreground">30% deposit</span><span className="text-charcoal" data-testid="dc-deposit-line">${order.direct.deposit_amount_usd.toFixed(2)} · {order.direct.deposit.status}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">70% balance</span><span className="text-charcoal" data-testid="dc-balance-line">${order.direct.balance_amount_usd.toFixed(2)} · {order.direct.balance.status}</span></p>
                  {order.direct.confirmed_at && <p className="text-xs text-muted-foreground">Confirmed {new Date(order.direct.confirmed_at).toLocaleString()}</p>}
                  {order.direct.cancel_self_serve_until && <p className="text-xs text-muted-foreground" data-testid="dc-self-serve-until">Self-service refund until {new Date(order.direct.cancel_self_serve_until).toLocaleString()}</p>}
                  {order.direct.cancel_admin_window_until && <p className="text-xs text-muted-foreground" data-testid="dc-admin-window-until">Admin window until {new Date(order.direct.cancel_admin_window_until).toLocaleString()}</p>}
                  {order.direct.refund && (
                    <p className="text-xs text-status-available" data-testid="dc-refund-line">
                      Refunded: {Array.isArray(order.direct.refund.items)
                        ? (order.direct.refund.items.length ? order.direct.refund.items.map((v) => `${v.phase} $${Number(v.amount_usd).toFixed(2)} (${v.status})`).join(" · ") : `$${Number(order.direct.refund.total_usd || 0).toFixed(2)} (no Stripe charge to refund)`)
                        : Object.entries(order.direct.refund).map(([k, v]) => `${k} $${Number(v?.amount_usd || 0).toFixed(2)} (${v?.status || ""})`).join(" · ")}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {order.direct_allocation && (
            <Card className="border-border bg-white" data-testid="dc-allocation-card">
              <CardContent className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Factory allocation</p>
                <p className="mt-3 text-sm text-charcoal" data-testid="dc-allocation-summary">
                  {order.direct_allocation.active_boxes} active · {order.direct_allocation.released_boxes} released · {order.direct_allocation.retained_boxes} retained (boxes)
                </p>
                <div className="mt-2 space-y-1">
                  {order.direct_allocation.lines.map((a) => (
                    <p key={a.line_id} className="text-xs text-muted-foreground" data-testid={`dc-alloc-${a.line_id}`}>
                      {a.variant_label} × {a.boxes} — {a.status}{a.release_reason ? ` (${a.release_reason})` : ""}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {order.shipments?.length > 0 && (
            <Card className="border-border bg-white" data-testid="admin-order-shipments">
              <CardContent className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Shipments</p>
                <div className="mt-3 space-y-3">
                  {order.shipments.map((s, i) => (
                    <div key={s.id} className="rounded-md border border-border bg-background p-3" data-testid={`admin-shipment-${i}`}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-terracotta">Shipment {i + 1} · {s.shipment_type}</p>
                      <p className="mt-1 text-sm font-medium text-charcoal">{s.carrier}{s.service ? ` · ${s.service}` : ""}</p>
                      {s.tracking_number && <p className="mt-0.5 font-mono text-xs text-charcoal">{s.shipment_type === "freight" ? "PRO" : "Tracking"}: {s.tracking_number}</p>}
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {s.pieces != null ? `${s.pieces} ${s.shipment_type === "freight" ? "pallet(s)" : "package(s)"} · ` : ""}
                        Shipped {new Date(s.shipped_at).toLocaleDateString()}
                        {s.estimated_delivery ? ` · ETA ${new Date(s.estimated_delivery + "T00:00:00").toLocaleDateString()}` : ""}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">Lines: {s.line_ids.map((lid) => order.lines.find((l) => l.line_id === lid)?.variant_label).filter(Boolean).join(", ")}</p>
                      <p className="mt-1 text-xs font-medium text-charcoal" data-testid={`admin-shipment-status-${i}`}>Status: {s.status_label}{s.events?.length ? ` · ${s.events.length} event(s)` : ""}</p>
                      {s.events?.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground" data-testid={`admin-shipment-events-${i}`}>
                          {s.events.map((ev, j) => <li key={j}>{new Date(ev.occurred_at).toLocaleString()} · {ev.state} · {ev.source}{ev.note ? ` — ${ev.note}` : ""}</li>)}
                        </ul>
                      )}
                      {s.tracking_status !== "DELIVERED" && (
                        evtForm.sid === s.id ? (
                          <form className="mt-2 flex flex-wrap items-end gap-2" data-testid={`shipment-event-form-${i}`} onSubmit={async (e) => { e.preventDefault(); setSaving(true); try { await api.post(`/admin/orders/${id}/shipments/${s.id}/events`, { state: evtForm.state, source: evtForm.source, note: evtForm.note }); toast.success("Shipment event recorded"); setEvtForm({ sid: null, state: "IN_TRANSIT", source: "manual", note: "" }); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); } }}>
                            <select className="h-9 rounded-md border border-input bg-white px-2 text-xs" value={evtForm.state} onChange={(e) => setEvtForm({ ...evtForm, state: e.target.value, source: e.target.value === "HANDED_OFF" ? "warehouse" : evtForm.source })} data-testid="shipment-event-state">
                              {s.tracking_status === "PACKED" ? <option value="HANDED_OFF">Carrier handoff confirmed (dispatch)</option> : <>
                                <option value="IN_TRANSIT">In transit</option><option value="DELIVERED">Delivered</option><option value="EXCEPTION">Exception</option></>}
                            </select>
                            <select className="h-9 rounded-md border border-input bg-white px-2 text-xs" value={evtForm.state === "HANDED_OFF" ? "warehouse" : evtForm.source} disabled={evtForm.state === "HANDED_OFF" || s.tracking_status === "PACKED"} onChange={(e) => setEvtForm({ ...evtForm, source: e.target.value })} data-testid="shipment-event-source">
                              <option value="warehouse">Warehouse</option><option value="carrier">Carrier evidence</option><option value="manual">Manual (documented)</option>
                            </select>
                            <Input className="h-9 w-56 bg-white text-xs" placeholder="Note / evidence" value={evtForm.note} onChange={(e) => setEvtForm({ ...evtForm, note: e.target.value })} data-testid="shipment-event-note" />
                            <Button type="submit" size="sm" disabled={saving} data-testid="shipment-event-submit">Record</Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setEvtForm({ sid: null, state: "IN_TRANSIT", source: "manual", note: "" })}>Cancel</Button>
                          </form>
                        ) : (
                          <button type="button" className="mt-1 mr-3 text-xs font-semibold text-terracotta hover:text-terracotta-hover" data-testid={`shipment-record-event-${i}`} onClick={() => setEvtForm({ sid: s.id, state: s.tracking_status === "PACKED" ? "HANDED_OFF" : "IN_TRANSIT", source: s.tracking_status === "PACKED" ? "warehouse" : "manual", note: "" })}>
                            {s.tracking_status === "PACKED" ? "Confirm carrier handoff →" : "Record shipment event →"}
                          </button>
                        )
                      )}
                      {order.ship_to?.kind === "direct_to_client" && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${order.id}/shipments/${s.id}/client-slip.pdf`} target="_blank" rel="noopener noreferrer" data-testid={`admin-client-slip-${i}`} className="mt-1 mr-3 inline-block text-xs font-semibold text-charcoal hover:text-terracotta">Client slip (PDF) →</a>
                      )}
                      {s.tracking_url && <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`admin-track-${i}`} className="mt-1 inline-block text-xs font-semibold text-terracotta hover:text-terracotta-hover">Track →</a>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-border bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Timeline</p>
              <div className="mt-3 space-y-2" data-testid="admin-order-events">
                {order.events.map((e, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-charcoal">{e.status}</span> · {new Date(e.at).toLocaleString()}
                    {e.note ? ` — ${e.note}` : ""}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={overrideOpen} onOpenChange={(o) => !o && setOverrideOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Adjust pricing</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              One-off override for this order only — snapshotted onto the lines and audited. Historical orders are never recalculated.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitOverride} className="mt-2 space-y-4" data-testid="price-override-form">
            {order.lines.map((l) => (
              <div key={l.line_id} className="flex items-center justify-between gap-3">
                <p className="text-xs text-charcoal">{l.variant_label} · {l.boxes} boxes</p>
                <Input type="number" step="0.01" min="0" value={ovPrices[l.line_id] ?? ""} onChange={(e) => setOvPrices((p) => ({ ...p, [l.line_id]: e.target.value }))} data-testid={`override-price-${l.line_id}`} className="h-10 w-28 bg-white focus-visible:ring-terracotta" />
              </div>
            ))}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason (audited)</Label>
              <Input required value={ovReason} onChange={(e) => setOvReason(e.target.value)} data-testid="override-reason" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            {ovWarnings.length > 0 && (
              <label className="flex items-start gap-2 rounded-md border border-status-sold/40 bg-[#D4A373]/10 p-3 text-xs text-[#8a6435]" data-testid="override-warning-row">
                <input type="checkbox" checked={ovAck} onChange={(e) => setOvAck(e.target.checked)} data-testid="override-ack" className="mt-0.5" />
                Below the configured floor/target margin. I explicitly confirm this commercial decision.
              </label>
            )}
            <Button type="submit" disabled={saving || (ovWarnings.length > 0 && !ovAck)} data-testid="override-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply override"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={freightOpen} onOpenChange={(o) => !o && setFreightOpen(false)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Set freight &amp; logistics</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Charged separately from the product subtotal. Only editable before payment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitFreight} className="mt-2 space-y-4" data-testid="freight-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Freight amount (USD)</Label>
              <Input required type="number" step="0.01" min="0" value={freightForm.amount} onChange={(e) => setFreightForm((f) => ({ ...f, amount: e.target.value }))} data-testid="freight-amount" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Note</Label>
              <Input value={freightForm.note} onChange={(e) => setFreightForm((f) => ({ ...f, note: e.target.value }))} data-testid="freight-note" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving} data-testid="freight-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save freight quote"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={shipOpen} onOpenChange={(o) => !o && setShipOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md sm:!max-h-[85dvh] sm:!overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Ship {selected.length} line{selected.length > 1 ? "s" : ""}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              One physical shipment — reduces physical and committed inventory atomically for every selected line.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleShip} className="mt-2 space-y-4" data-testid="ship-form">
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-sand p-1" data-testid="ship-type-toggle">
              <Button type="button" data-testid="ship-type-parcel" onClick={() => setShipForm((f) => ({ ...f, shipment_type: "parcel" }))}
                className={`h-9 transition-colors ${shipForm.shipment_type === "parcel" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal hover:bg-limestone"}`}>Parcel</Button>
              <Button type="button" data-testid="ship-type-freight" onClick={() => setShipForm((f) => ({ ...f, shipment_type: "freight" }))}
                className={`h-9 transition-colors ${shipForm.shipment_type === "freight" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal hover:bg-limestone"}`}>Freight / LTL</Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Carrier</Label>
              <Input data-testid="ship-carrier" list="order-carrier-presets" value={shipForm.carrier} onChange={setShipField("carrier")} className="h-11 bg-white focus-visible:ring-terracotta" />
              <datalist id="order-carrier-presets">
                {["UPS", "USPS", "FedEx", "DHL", "FedEx Freight", "Other"].map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Service (optional)</Label>
              <Input data-testid="ship-service" value={shipForm.service} onChange={setShipField("service")} placeholder="Ground, Priority, LTL…" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{shipForm.shipment_type === "freight" ? "PRO number" : "Tracking number"} *</Label>
              <Input data-testid="ship-tracking" value={shipForm.tracking_number} onChange={setShipField("tracking_number")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{shipForm.shipment_type === "freight" ? "Pallets" : "Packages"} (optional)</Label>
                <Input type="number" min="1" step="1" inputMode="numeric" data-testid="ship-pieces" value={shipForm.pieces} onChange={setShipField("pieces")} className="h-11 bg-white focus-visible:ring-terracotta" />
                {shipForm.shipment_type === "freight" && <p className="text-[11px] text-warmgrey">Whole pallets only — a pallet may hold any number of boxes.</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Est. delivery (optional)</Label>
                <DatePickerField value={shipForm.estimated_delivery} min={todayLocal()} testId="ship-eta" placeholder="Estimated delivery" onChange={(v) => setShipForm((f) => ({ ...f, estimated_delivery: v }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Carrier tracking URL (optional — freight PRO / carriers without automatic links)</Label>
              <Input type="url" placeholder="https://…" data-testid="ship-tracking-url" value={shipForm.tracking_url || ""} onChange={setShipField("tracking_url")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border bg-background p-3 text-xs text-charcoal" data-testid="handoff-confirmed-row">
              <input type="checkbox" data-testid="handoff-confirmed-checkbox" checked={handoffConfirmed} onChange={(e) => setHandoffConfirmed(e.target.checked)} className="mt-0.5" />
              <span>Warehouse confirms the carrier has physically taken this shipment (dispatch — deducts on-hand once). Uncheck to record a packed shipment / label only; record the handoff later from the shipment card.</span>
            </label>
            {othersAwaiting && (
              <label className="flex items-start gap-2 rounded-md border border-terracotta/40 bg-background p-3 text-xs text-charcoal" data-testid="split-shipment-row">
                <input type="checkbox" data-testid="split-shipment-checkbox" checked={splitApproved} onChange={(e) => setSplitApproved(e.target.checked)} className="mt-0.5" />
                Split shipment approved — other lines are still awaiting container receipt.
              </label>
            )}
            <Button type="submit" disabled={saving || selected.length === 0 || !shipForm.tracking_number.trim() || (othersAwaiting && !splitApproved)} data-testid="ship-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ship"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">
              {dialog === "release" ? "Release order" : dialog === "refund" ? "Refund order" : "Resolve review"}
            </DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              {dialog === "release" && "Releases the committed inventory back to available — exactly once."}
              {dialog === "refund" && "Initiates a full Stripe refund. Committed inventory releases when the refund confirms; physical is unchanged (goods never shipped)."}
              {dialog === "review" && "Choose how to resolve this admin review."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAction} className="mt-2 space-y-5" data-testid="order-action-form">
            {dialog === "review" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Action</Label>
                <Select value={reviewAction} onValueChange={setReviewAction}>
                  <SelectTrigger data-testid="review-action-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="extend">Keep waiting (extend ACH deadline)</SelectItem>
                    <SelectItem value="release">Release inventory & cancel</SelectItem>
                    <SelectItem value="confirm_paid">Confirm paid manually</SelectItem>
                  </SelectContent>
                </Select>
                {reviewAction === "extend" && (
                  <Input type="number" min="1" max="30" data-testid="review-extend-days" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="h-11 bg-white focus-visible:ring-terracotta" />
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason / note</Label>
              <Input data-testid="order-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving} data-testid="order-action-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dcDialog} onOpenChange={(o) => !o && setDcDialog(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">
              {dcDialog === "approve" ? "Approve cancellation request" : dcDialog === "decline" ? "Decline cancellation request" : "Cancel container order"}
            </DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              {dcDialog === "decline"
                ? "The order stays active and the dealer is notified."
                : "Record the financial and allocation treatment explicitly — everything is audited with actor and timestamp."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDc} className="mt-2 space-y-4" data-testid="dc-action-form">
            {dcDialog === "dccancel" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason (required, audited)</Label>
                <Input required value={dcForm.reason} onChange={(e) => setDcForm((f) => ({ ...f, reason: e.target.value }))} data-testid="dc-cancel-reason" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            )}
            {dcDialog === "decline" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason shown to the dealer</Label>
                <Input value={dcForm.reason} onChange={(e) => setDcForm((f) => ({ ...f, reason: e.target.value }))} data-testid="dc-decline-reason" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            )}
            {(dcDialog === "approve" || dcDialog === "dccancel") && order.direct && (
              <>
                {order.direct.deposit.status === "PAID" && (
                  <label className="flex items-start gap-2 rounded-md border border-border bg-background p-3 text-xs text-charcoal" data-testid="dc-refund-deposit-row">
                    <input type="checkbox" checked={dcForm.refund_deposit} onChange={(e) => setDcForm((f) => ({ ...f, refund_deposit: e.target.checked }))} data-testid="dc-refund-deposit" className="mt-0.5" />
                    Refund the 30% deposit (${order.direct.deposit_amount_usd.toFixed(2)}) to the original payment method — exception, audited
                  </label>
                )}
                {order.direct.balance.status === "PAID" && (
                  <label className="flex items-start gap-2 rounded-md border border-border bg-background p-3 text-xs text-charcoal" data-testid="dc-refund-balance-row">
                    <input type="checkbox" checked={dcForm.refund_balance} onChange={(e) => setDcForm((f) => ({ ...f, refund_balance: e.target.checked }))} data-testid="dc-refund-balance" className="mt-0.5" />
                    Refund the 70% balance (${order.direct.balance_amount_usd.toFixed(2)}) — exception, audited
                  </label>
                )}
                <label className="flex items-start gap-2 rounded-md border border-border bg-background p-3 text-xs text-charcoal" data-testid="dc-release-row">
                  <input type="checkbox" checked={dcForm.release_allocation} onChange={(e) => setDcForm((f) => ({ ...f, release_allocation: e.target.checked }))} data-testid="dc-release-allocation" className="mt-0.5" />
                  Release the factory/container allocation (uncheck only if irrevocably committed)
                </label>
                {!dcForm.release_allocation && (
                  <Input value={dcForm.allocation_note} onChange={(e) => setDcForm((f) => ({ ...f, allocation_note: e.target.value }))} placeholder="Why is the allocation retained? (audited)" data-testid="dc-allocation-note" className="h-11 bg-white focus-visible:ring-terracotta" />
                )}
                {order.direct.cancel_admin_window_until && Date.now() >= new Date(order.direct.cancel_admin_window_until).getTime() && (
                  <label className="flex items-start gap-2 rounded-md border border-status-sold/40 bg-[#D4A373]/10 p-3 text-xs text-[#8a6435]" data-testid="dc-exception-row">
                    <input type="checkbox" checked={dcForm.exception_ack} onChange={(e) => setDcForm((f) => ({ ...f, exception_ack: e.target.checked }))} data-testid="dc-exception-ack" className="mt-0.5" />
                    Past the 168-hour boundary — I explicitly confirm this administrative exception with the treatments recorded above.
                  </label>
                )}
              </>
            )}
            {dcDialog === "approve" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Note (audited)</Label>
                <Input value={dcForm.note} onChange={(e) => setDcForm((f) => ({ ...f, note: e.target.value }))} data-testid="dc-approve-note" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            )}
            <Button type="submit" disabled={saving} data-testid="dc-action-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
